import React, { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ChartBarSquareIcon,
  CpuChipIcon,
  DevicePhoneMobileIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  PresentationChartLineIcon,
  BuildingOffice2Icon,
  ChatBubbleBottomCenterTextIcon,
  Squares2X2Icon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  CubeIcon,
  StarIcon,
  ClipboardDocumentCheckIcon,
  LightBulbIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { Text, Button, Heading } from '../ui'
import { QgNumerosRow } from '../../types/qgNumeros'
import { DashboardCardSkeleton } from './DashboardSkeleton'
import clsx from 'clsx'

const numberFormatter = new Intl.NumberFormat('pt-BR')

interface KpiItem {
  label: string
  value: number
  icon: React.ReactNode
}

interface KpiSection {
  id: string
  title: string
  icon: React.ReactNode
  items: KpiItem[]
}

const AnimatedValue: React.FC<{ value: number; active: boolean }> = ({ value, active }) => {
  const [display, setDisplay] = useState(0)

  useEffect(() => {
    if (!active) {
      setDisplay(0)
      return
    }

    const duration = 800
    const start = performance.now()
    let frame: number

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplay(Math.round(value * eased))
      if (progress < 1) frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [value, active])

  return <>{numberFormatter.format(display)}</>
}

function buildSections(data: QgNumerosRow): KpiSection[] {
  return [
    {
      id: 'atendimentos',
      title: 'Atendimentos',
      icon: <CpuChipIcon className="h-4 w-4" />,
      items: [
        {
          label: 'Analisados pela IA',
          value: data.total_ai_ticket_analyses,
          icon: <CpuChipIcon className="h-5 w-5" />,
        },
        {
          label: 'Importados do WhatsApp',
          value: data.total_whatsapp_tickets,
          icon: <DevicePhoneMobileIcon className="h-5 w-5" />,
        },
      ],
    },
    {
      id: 'qualidade',
      title: 'Qualidade',
      icon: <ClipboardDocumentCheckIcon className="h-4 w-4" />,
      items: [
        {
          label: 'Tickets de qualidade',
          value: data.total_quality_tickets ?? 0,
          icon: <ClipboardDocumentCheckIcon className="h-5 w-5" />,
        },
        {
          label: 'Tickets de melhoria',
          value: data.total_quality_improvement_tickets ?? 0,
          icon: <LightBulbIcon className="h-5 w-5" />,
        },
      ],
    },
    {
      id: 'cct',
      title: 'Convenções',
      icon: <MagnifyingGlassIcon className="h-4 w-4" />,
      items: [
        {
          label: 'Buscas de CCTs realizadas',
          value: data.total_cct_mte_searches,
          icon: <MagnifyingGlassIcon className="h-5 w-5" />,
        },
        {
          label: 'CCTs importadas para o SCGP',
          value: data.total_ccts_imported_scgp,
          icon: <ArrowDownTrayIcon className="h-5 w-5" />,
        },
      ],
    },
    {
      id: 'dashboards',
      title: 'Dashboards',
      icon: <PresentationChartLineIcon className="h-4 w-4" />,
      items: [
        {
          label: 'Acessos ao Dash de Campanhas',
          value: data.total_campaign_dashboard_accesses,
          icon: <PresentationChartLineIcon className="h-5 w-5" />,
        },
        {
          label: 'Acessos ao Dash do Cliente',
          value: data.total_client_dashboard_accesses,
          icon: <BuildingOffice2Icon className="h-5 w-5" />,
        },
      ],
    },
    {
      id: 'pasigpt',
      title: 'PasiGPT',
      icon: <ChatBubbleBottomCenterTextIcon className="h-4 w-4" />,
      items: [
        {
          label: 'Interações no PasiGPT',
          value: data.total_pasigpt_interactions,
          icon: <ChatBubbleBottomCenterTextIcon className="h-5 w-5" />,
        },
      ],
    },
    {
      id: 'superapp',
      title: 'SuperAPP',
      icon: <Squares2X2Icon className="h-4 w-4" />,
      items: [
        {
          label: 'Usuários do SuperAPP',
          value: data.total_superapp_users ?? 0,
          icon: <Squares2X2Icon className="h-5 w-5" />,
        },
      ],
    },
    {
      id: 'shop',
      title: 'Pasi Shop',
      icon: <ShoppingBagIcon className="h-4 w-4" />,
      items: [
        {
          label: 'Acessos ao Pasi Shop',
          value: data.total_shop_accesses,
          icon: <ShoppingBagIcon className="h-5 w-5" />,
        },
        {
          label: 'Número de pedidos',
          value: data.total_shop_orders ?? 0,
          icon: <ShoppingCartIcon className="h-5 w-5" />,
        },
        {
          label: 'Produtos trocados',
          value: data.total_shop_order_items_quantity ?? 0,
          icon: <CubeIcon className="h-5 w-5" />,
        },
        {
          label: 'PASI Points trocados',
          value: data.total_shop_points_spent ?? 0,
          icon: <StarIcon className="h-5 w-5" />,
        },
      ],
    },
  ]
}

function formatUpdatedAt(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const KpiTile: React.FC<{ item: KpiItem; animate: boolean; delay?: number }> = ({
  item,
  animate,
  delay = 0,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay, ease: [0.22, 1, 0.36, 1] }}
    className={clsx(
      'group flex items-center gap-3 rounded-xl border p-3.5',
      'border-primary-500/15 bg-white dark:bg-gray-800/80',
      'shadow-sm hover:shadow-md hover:border-primary-500/35',
      'transition-all duration-200 hover:-translate-y-0.5'
    )}
  >
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300 transition-transform duration-200 group-hover:scale-105">
      {item.icon}
    </span>
    <div className="min-w-0 flex-1">
      <Text size="xs" color="muted" className="font-medium leading-snug line-clamp-2">
        {item.label}
      </Text>
      <p className="mt-0.5 text-xl font-bold tabular-nums tracking-tight text-primary-600 dark:text-primary-300">
        <AnimatedValue value={item.value} active={animate} />
      </p>
    </div>
  </motion.div>
)

const SectionBlock: React.FC<{ section: KpiSection; animate: boolean; index: number }> = ({
  section,
  animate,
  index,
}) => (
  <motion.section
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay: 0.15 + index * 0.05 }}
    className="min-w-0"
  >
    <div className="mb-2.5 flex items-center gap-2">
      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-50 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
        {section.icon}
      </span>
      <Text size="sm" weight="semibold" className="text-gray-900 dark:text-white">
        {section.title}
      </Text>
      <span className="h-px flex-1 bg-gradient-to-r from-gray-200 to-transparent dark:from-gray-700" aria-hidden />
    </div>
    <div
      className={clsx(
        'grid gap-2.5',
        section.items.length === 1 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        section.items.length === 2 && 'grid-cols-1 sm:grid-cols-2',
        section.items.length === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        section.items.length >= 4 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
      )}
    >
      {section.items.map((item, i) => (
        <KpiTile key={item.label} item={item} animate={animate} delay={0.05 * i} />
      ))}
    </div>
  </motion.section>
)

interface QGNumerosModalProps {
  isOpen: boolean
  onClose: () => void
  data: QgNumerosRow | null
  loading: boolean
  error: string | null
}

const QGNumerosModal: React.FC<QGNumerosModalProps> = ({
  isOpen,
  onClose,
  data,
  loading,
  error,
}) => {
  const sections = useMemo(() => (data ? buildSections(data) : []), [data])

  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const hasContent = !loading && !error && !!data && sections.length > 0

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden p-2 sm:p-4"
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />

      <div
        className={clsx(
          'relative z-10 flex h-full w-full max-w-5xl flex-col',
          'max-h-full',
          'bg-gray-50 dark:bg-gray-900',
          'rounded-2xl shadow-2xl',
          'overflow-hidden ring-1 ring-black/5 dark:ring-white/10'
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="qg-numeros-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 border-b border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-800">
          <div className="flex items-start gap-3 px-4 py-4 sm:px-6 sm:py-5">
            <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-500 text-white shadow-sm shadow-primary-500/25">
              <ChartBarSquareIcon className="h-6 w-6" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <Heading
                  as="h2"
                  size="2xl"
                  id="qg-numeros-modal-title"
                  className="text-gray-900 dark:text-white tracking-tight"
                >
                  Números do PASI QG
                </Heading>
                {data?.updated_at && !loading && !error && (
                  <Text size="sm" color="muted" className="tabular-nums">
                    Atualizado em {formatUpdatedAt(data.updated_at)}
                  </Text>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="!p-2 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100"
              aria-label="Fechar"
            >
              <XMarkIcon className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5">
          {loading && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <DashboardCardSkeleton key={i} className="h-24 rounded-xl" />
                ))}
              </div>
              <div className="space-y-3">
                <DashboardCardSkeleton className="h-5 w-32" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <DashboardCardSkeleton className="h-20 rounded-xl" />
                  <DashboardCardSkeleton className="h-20 rounded-xl" />
                </div>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="rounded-xl border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 p-4">
              <Text size="sm" className="text-yellow-800 dark:text-yellow-200">
                {error}
              </Text>
            </div>
          )}

          {hasContent && (
            <AnimatePresence mode="wait">
              <motion.div
                key="qg-numeros-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                <div className="space-y-5">
                  {sections.map((section, index) => (
                    <SectionBlock
                      key={section.id}
                      section={section}
                      animate
                      index={index}
                    />
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          )}

          {!loading && !error && !data && (
            <div className="py-12 text-center">
              <ChartBarSquareIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
              <Text size="sm" color="muted" className="mt-3">
                Nenhum indicador disponível no momento.
              </Text>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 flex items-center justify-end">
          <Button
            type="button"
            variant="solid"
            size="sm"
            onClick={onClose}
            className="border-0 bg-primary-500 text-white shadow-sm ring-0 hover:bg-primary-600 focus:ring-primary-500/25 dark:bg-primary-500 dark:text-white dark:hover:bg-primary-400"
          >
            Fechar
          </Button>
        </div>
      </div>
    </div>
  )
}

export default QGNumerosModal
